(() => {
  if (window.__sbCarouselScriptLoaded) return;

  const cleanups = new WeakMap();

  const initCarousels = (scope = document) => {
    const roots = scope.querySelectorAll('[data-sb-carousel]');
    if (!roots.length) return;

    roots.forEach((root) => {
      if (root.dataset.sbCarouselInit === 'true') return;

      const viewport = root.querySelector('[data-sb-carousel-viewport]');
      const track = root.querySelector('[data-sb-carousel-track]');
      const nativeEndOffsetSpacer = track ? track.querySelector('[data-sb-carousel-end-offset]') : null;
      const prev = root.querySelector('[data-sb-carousel-prev]');
      const next = root.querySelector('[data-sb-carousel-next]');

      if (!viewport || !track || !prev || !next) return;
      root.dataset.sbCarouselInit = 'true';

      const controller = new AbortController();
      const listen = (target, type, handler, options = {}) => {
        target.addEventListener(type, handler, { ...options, signal: controller.signal });
      };

      const px = (value) => {
        const n = Number.parseFloat(value || '0');
        return Number.isFinite(n) ? n : 0;
      };

      const pageMargin = () => {
        const styles = getComputedStyle(document.documentElement);
        const desktop = window.matchMedia('(min-width: 750px)').matches;
        const token = desktop ? '--page-margin-desktop' : '--page-margin-mobile';
        return px(styles.getPropertyValue(token)) || px(styles.getPropertyValue('--page-margin')) || 16;
      };
      const layoutViewportWidth = () => Math.max(
        1,
        document.documentElement.clientWidth || window.innerWidth || 0
      );
      const contentWidth = () => Math.max(1, root.getBoundingClientRect().width || root.clientWidth || 0);
      const viewportPaddingStart = () => px(getComputedStyle(viewport).paddingLeft);
      const viewportPaddingEnd = () => px(getComputedStyle(viewport).paddingRight);
      const items = () => track.querySelectorAll('[data-sb-carousel-item]');
      const itemStartOffset = (item) =>
        Math.max(0, item.getBoundingClientRect().left - track.getBoundingClientRect().left);
      const itemEndOffset = (item) =>
        Math.max(
          0,
          itemStartOffset(item)
            + viewportPaddingStart()
            + item.getBoundingClientRect().width
            - (viewport.clientWidth - viewportPaddingEnd())
        );

      const scrollEpsilon = 2;
      const snapTolerance = 24;
      const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const shouldClampToBounds = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      let maxScroll = 0;
      let layoutSnapPoints = [0];
      let syncFrame = 0;
      let pendingTarget = null;

      const currentMaxScroll = () => maxScroll;
      const currentScrollOffset = () => Math.max(0, Math.min(viewport.scrollLeft, currentMaxScroll()));
      const effectiveScrollOffset = () =>
        pendingTarget === null
          ? currentScrollOffset()
          : Math.max(0, Math.min(pendingTarget, currentMaxScroll()));
      const clearNativeEndOffset = () => {
        track.style.removeProperty('--sb-carousel-native-end-offset');
        track.style.paddingRight = '';
        track.style.marginRight = '';
        if (nativeEndOffsetSpacer instanceof HTMLElement) {
          nativeEndOffsetSpacer.style.flexBasis = '';
          nativeEndOffsetSpacer.style.marginLeft = '';
          nativeEndOffsetSpacer.style.width = '';
        }
      };
      const requestSyncState = () => {
        if (syncFrame) return;
        syncFrame = window.requestAnimationFrame(() => {
          syncFrame = 0;
          syncState();
        });
      };
      const getSnapPoints = (max) => {
        const list = items();
        if (!list.length) return [0, max];

        const points = Array.from(list).map((item) => itemStartOffset(item));
        points.push(max);

        const normalized = points
          .map((point) => Math.max(0, Math.min(max, point)))
          .map((point) => Number(point.toFixed(2)));

        return Array.from(new Set(normalized)).sort((a, b) => a - b);
      };
      const getClosestSnap = (offset) => {
        // Identify the item using positions from before the responsive resize.
        const points = layoutSnapPoints;
        if (!points.length) return { index: 0, point: 0, distance: Math.abs(offset) };

        let closestIndex = 0;
        let closestPoint = points[0];
        let closestDistance = Math.abs(points[0] - offset);

        points.forEach((point, index) => {
          const distance = Math.abs(point - offset);
          if (distance < closestDistance) {
            closestIndex = index;
            closestPoint = point;
            closestDistance = distance;
          }
        });

        return { index: closestIndex, point: closestPoint, distance: closestDistance };
      };
      const captureLayoutState = () => {
        const max = currentMaxScroll();
        const offset = effectiveScrollOffset();

        if (max <= scrollEpsilon || offset <= scrollEpsilon) {
          return { type: 'start' };
        }

        // A wider viewport can clamp scrollLeft before the resize callback.
        // Recognize that native end before choosing a nearby item snap.
        const nativeMax = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        if (offset >= Math.min(max, nativeMax) - scrollEpsilon) return { type: 'end' };

        const { index, point, distance } = getClosestSnap(offset);
        const list = items();
        const isEndSnap = point >= max - scrollEpsilon || index >= list.length;

        if (pendingTarget !== null || distance <= snapTolerance) {
          if (isEndSnap) return { type: 'end' };
          return { type: 'item', index };
        }

        return { type: 'offset', value: offset };
      };
      const resolveLayoutState = (state, max) => {
        if (!state || max <= scrollEpsilon) return 0;
        if (state.type === 'start') return 0;
        if (state.type === 'end') return max;
        if (state.type === 'offset') return Math.max(0, Math.min(state.value, max));

        const list = Array.from(items());
        if (!list.length) return 0;

        const index = Math.max(0, Math.min(state.index, list.length - 1));
        return Math.max(0, Math.min(itemStartOffset(list[index]), max));
      };

      const getNextSnap = (current, max) => {
        const points = getSnapPoints(max);
        for (const point of points) {
          if (point > current + scrollEpsilon) return point;
        }
        return max;
      };

      const getPrevSnap = (current, max) => {
        const points = getSnapPoints(max);
        for (let i = points.length - 1; i >= 0; i -= 1) {
          if (points[i] < current - scrollEpsilon) return points[i];
        }
        return 0;
      };

      const setBleed = () => {
        const bleed = Math.max(pageMargin(), Math.floor((layoutViewportWidth() - contentWidth()) / 2));
        root.style.setProperty('--sb-carousel-bleed-inline', `${Math.max(0, bleed)}px`);
      };

      const syncState = () => {
        const max = currentMaxScroll();
        const overflowing = max > 1;
        if (overflowing && shouldClampToBounds()) {
          if (viewport.scrollLeft > max + scrollEpsilon) viewport.scrollLeft = max;
          if (viewport.scrollLeft < -scrollEpsilon) viewport.scrollLeft = 0;
        }
        const current = currentScrollOffset();
        const effectiveCurrent = effectiveScrollOffset();
        root.classList.toggle('is-overflowing', overflowing);
        root.classList.toggle('is-not-overflowing', !overflowing);

        if (!overflowing) {
          if (Math.abs(viewport.scrollLeft) > scrollEpsilon) viewport.scrollLeft = 0;
          pendingTarget = null;
          prev.disabled = true;
          next.disabled = true;
          return;
        }

        prev.disabled = effectiveCurrent <= scrollEpsilon;
        next.disabled = effectiveCurrent >= max - scrollEpsilon;

        if (pendingTarget !== null && Math.abs(current - pendingTarget) <= scrollEpsilon) {
          pendingTarget = null;
        }
      };

      const refreshLayout = ({ preserveState = null } = {}) => {
        // Tab switches resize hidden rows to zero. They need no layout work.
        if (root.closest('.sb-segment-hidden') || !root.getClientRects().length) return;
        setBleed();
        clearNativeEndOffset();
        track.style.transform = '';

        const list = items();
        const lastItem = list[list.length - 1];
        const desiredMax = lastItem ? itemEndOffset(lastItem) : 0;
        const baseNativeMax = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        const nativeEndOffset = Math.max(0, desiredMax - baseNativeMax);

        if (nativeEndOffset > scrollEpsilon) {
          if (nativeEndOffsetSpacer instanceof HTMLElement) {
            const gap = px(getComputedStyle(track).gap);
            nativeEndOffsetSpacer.style.flexBasis = `${nativeEndOffset}px`;
            nativeEndOffsetSpacer.style.marginLeft = `${gap > 0 ? -gap : 0}px`;
            nativeEndOffsetSpacer.style.width = `${nativeEndOffset}px`;

            const adjustedNativeMax = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
            const remainingShortfall = Math.max(0, desiredMax - adjustedNativeMax);
            if (remainingShortfall > scrollEpsilon) {
              nativeEndOffsetSpacer.style.flexBasis = `${nativeEndOffset + remainingShortfall}px`;
              nativeEndOffsetSpacer.style.width = `${nativeEndOffset + remainingShortfall}px`;
            }
          } else {
            track.style.paddingRight = `${nativeEndOffset}px`;

            const adjustedNativeMax = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
            const remainingShortfall = Math.max(0, desiredMax - adjustedNativeMax);
            if (remainingShortfall > scrollEpsilon) {
              track.style.paddingRight = `${nativeEndOffset + remainingShortfall}px`;
            }
          }
        }

        maxScroll = desiredMax;
        layoutSnapPoints = getSnapPoints(maxScroll);

        if (maxScroll <= scrollEpsilon) {
          if (Math.abs(viewport.scrollLeft) > scrollEpsilon) viewport.scrollLeft = 0;
        } else if (preserveState) {
          pendingTarget = null;
          viewport.scrollLeft = resolveLayoutState(preserveState, maxScroll);
        } else if (shouldClampToBounds() && viewport.scrollLeft > maxScroll) {
          viewport.scrollLeft = maxScroll;
        }

        syncState();
      };
      let layoutFrame = 0;
      const refreshLayoutPreservingState = () => {
        if (layoutFrame || root.closest('.sb-segment-hidden')) return;
        layoutFrame = window.requestAnimationFrame(() => {
          layoutFrame = 0;
          if (!root.isConnected || root.closest('.sb-segment-hidden') || !root.getClientRects().length) return;
          refreshLayout({ preserveState: captureLayoutState() });
        });
      };

      const scrollToOffset = (value, { animate = false } = {}) => {
        const max = currentMaxScroll();
        let clamped = Math.max(0, Math.min(value, max));
        if (clamped < scrollEpsilon) clamped = 0;
        if (clamped > max - scrollEpsilon) clamped = max;
        const startOffset = viewport.scrollLeft;
        const intendedDelta = Math.abs(clamped - startOffset);
        if (intendedDelta <= scrollEpsilon) return;

        if (animate && !prefersReducedMotion()) {
          pendingTarget = clamped;
          viewport.scrollTo({ left: clamped, behavior: 'smooth' });
          requestSyncState();
          return;
        }

        pendingTarget = null;
        viewport.scrollLeft = clamped;
        syncState();
      };

      const syncControlsForTarget = (target) => {
        const max = currentMaxScroll();
        const clamped = Math.max(0, Math.min(target, max));
        prev.disabled = clamped <= scrollEpsilon;
        next.disabled = clamped >= max - scrollEpsilon;
      };

      // Browsers (iOS Safari especially) restore the horizontal scroll
      // position of overflow containers on reload, history navigation, and
      // tab restore - sometimes asynchronously after scripts run - which
      // made rows appear pre-scrolled on a fresh view. Any offset that
      // appears before the user has touched this carousel is restoration
      // debris: snap back to the left edge. bfcache restores skip init and
      // never re-fire load, so back-navigation within a session keeps its
      // position (pageshow with persisted=true is deliberately ignored).
      let userInteracted = false;
      const markInteracted = () => {
        userInteracted = true;
        pendingTarget = null;
        requestSyncState();
      };
      listen(viewport, 'touchstart', markInteracted, { passive: true });
      listen(viewport, 'mousedown', markInteracted);
      const resetRestoredScroll = () => {
        if (userInteracted || Math.abs(viewport.scrollLeft) <= scrollEpsilon) return;
        pendingTarget = null;
        viewport.scrollLeft = 0;
        requestSyncState();
      };
      listen(window, 'load', resetRestoredScroll);
      listen(window, 'pageshow', (event) => {
        if (!event.persisted) resetRestoredScroll();
      });

      listen(prev, 'click', () => {
        markInteracted();
        const max = currentMaxScroll();
        const target = getPrevSnap(viewport.scrollLeft, max);
        syncControlsForTarget(target);
        scrollToOffset(target, { animate: true });
      });

      listen(next, 'click', () => {
        markInteracted();
        const max = currentMaxScroll();
        const target = getNextSnap(viewport.scrollLeft, max);
        syncControlsForTarget(target);
        scrollToOffset(target, { animate: true });
      });

      listen(
        viewport,
        'scroll',
        () => {
          requestSyncState();
        },
        { passive: true }
      );

      listen(
        viewport,
        'wheel',
        (event) => {
          if (!root.classList.contains('is-overflowing')) return;

          const horizontalDelta = event.deltaX;
          if (Math.abs(horizontalDelta) < 1) return;

          markInteracted();
          event.preventDefault();
          const max = currentMaxScroll();
          let nextOffset = Math.max(0, Math.min(viewport.scrollLeft + horizontalDelta, max));
          if (nextOffset < scrollEpsilon) nextOffset = 0;
          if (nextOffset > max - scrollEpsilon) nextOffset = max;
          viewport.scrollLeft = nextOffset;
        },
        { passive: false }
      );

      let observer;
      if ('ResizeObserver' in window) {
        observer = new ResizeObserver(() => refreshLayoutPreservingState());
        observer.observe(viewport);
        observer.observe(track);
      }

      track.querySelectorAll('img').forEach((img) => {
        if (!img.complete) {
          listen(img, 'load', refreshLayoutPreservingState, { once: true });
          listen(img, 'error', refreshLayoutPreservingState, { once: true });
        }
      });

      listen(window, 'load', refreshLayoutPreservingState);
      listen(window, 'resize', refreshLayoutPreservingState);
      cleanups.set(root, () => {
        controller.abort();
        observer?.disconnect();
        window.cancelAnimationFrame(syncFrame);
        window.cancelAnimationFrame(layoutFrame);
        delete root.dataset.sbCarouselInit;
        cleanups.delete(root);
      });
      resetRestoredScroll();
      refreshLayout();

    });
  };

  window.__sbCarouselScriptLoaded = true;
  initCarousels(document);
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-sb-carousel]').forEach((root) => {
      cleanups.get(root)?.();
    });
  });
  document.addEventListener('shopify:section:load', (event) => {
    initCarousels(event.target);
  });
})();
